import Vue from 'vue';
import {BallPulseLoader} from 'vue-loaders';
import VueSimpleSpinner from 'vue-simple-spinner';
import HttpMixin from '@/mixins/HttpMixin';
import CheckboxControl from "@/componentsReusable/CheckboxControl.vue";
import {DateTime} from "luxon";
import Personalization from "@/components/communications/components/Personalization.vue";
import {CommunicationType, UiMessage as CommunicationMessage} from '../CommunicationInterfaces';

interface UiConversation {
  threadId: number;
  title: string;
  messages: UiMessage[];
}

interface UiMessage {
  id: string;
  from: string;
  fromSystem: boolean;
  datetime: string;
  message: string;
}

export interface ConversationMessage {
  id: string;
  occurredOn: string;
  messageContent: string;
  threadId: number;
  threadTitle: string;
  contactFirstName: string;
  contactLastName: string;
  appUserId: number;
  employeeFirstName: string;
  employeeLastName: string;
  providerId: number;
  facilityContactId: number;
}

const PAGE_SIZE = 50;

export default Vue.component('message-convo', {
  props: {
    message: { type: Object },
  },
  name: 'MessageConvo',
  data () {
    return {
      loading: false,
      messages: [] as any[],
      conversations: [] as UiConversation[],
      newMessage: '',
      maxLength: 420,
      canLoadMore: true,
      lastOccurredOn: new Date().toISOString()
    }
  },
  components: {
    BallPulseLoader,
    VueSimpleSpinner,
    CheckboxControl,
    Personalization
  },
  mixins: [ HttpMixin ],
  watch: {
    async 'message.isExpanded'(newVal: boolean) {
      if (newVal) {
        await this.loadConversation(true);
      } else {
        this.lastOccurredOn = new Date().toISOString();
      }
    },
    newMessage() {
      if (this.newMessage.length > this.maxLength) {
        this.newMessage = this.newMessage.substring(0, this.maxLength);
      }
    }
  },
  methods: {
    async loadConversation(initialLoad = false) {
      if (!initialLoad && !this.canLoadMore) {
        return;
      }
      const data = await (this as any).httpGet(`/communications/conversations/${this.message.id}?lastOccurredOn=${this.lastOccurredOn}&pageSize=${PAGE_SIZE}`, 'Failed to retrieve Conversation.') as ConversationMessage[] || [];
      this.canLoadMore = data.length === PAGE_SIZE;
      if (data.length > 0) {
        this.lastOccurredOn = data[data.length - 1].occurredOn;
      }
      if (initialLoad) {
        this.setInitialData(data);
      } else {
        this.setLoadedData(data);
      }
      if (data.length > 1) {
        this.scrollMessageIntoView(data[data.length - 1].id);
      }
      this.newMessage = '';
    },
    setInitialData(messages: ConversationMessage[]) {
      this.conversations = this.convertMessages(messages);
    },
    convertMessages(messages: ConversationMessage[]): UiConversation[] {
      return messages.reverse().reduce((conversations: UiConversation[], message: ConversationMessage, index: number, source: ConversationMessage[]) => {
        let conversation;
        if (index === 0 || message.threadId !== source[index - 1].threadId) {
          conversation = { threadId: message.threadId, title: message.threadTitle || 'No Campaign', messages: [] };
          conversations.push(conversation);
        } else {
          conversation = conversations[conversations.length - 1];
        }
        conversation.messages.push(this.translateMessage(message));
        return conversations;
      }, [] as UiConversation[]);
    },
    setLoadedData(messages: ConversationMessage[]) {
      if (messages.length === 0) {
        return;
      }
      const firstConversation = this.conversations[0];
      const newConversations = this.convertMessages(messages);
      if (firstConversation && firstConversation.threadId === newConversations[newConversations.length - 1].threadId) {
        firstConversation.messages.unshift(...newConversations.pop()!.messages);
      }
      this.conversations.unshift(...newConversations);
    },
    translateMessage(message: ConversationMessage): UiMessage {
      return {
        id: message.id,
        from: message.appUserId ? `${message.employeeFirstName} ${message.employeeLastName}` : `${message.contactFirstName} ${message.contactLastName}`,
        fromSystem: Boolean(message.appUserId),
        datetime: DateTime.fromISO(message.occurredOn).toFormat('MM.dd.yyyy - hh:mm a'),
        message: message.messageContent
      }
    },
    scrollMessageIntoView(messageId: string) {
      if (this.conversations.length === 0) {
        return;
      }
      this.$nextTick(() => {
        // Smooth scrolls messages to the bottom, most recent message.
        const scrollableMessageArea = this.$refs['scrollable-message-area'] as Element;
        scrollableMessageArea.scrollTo({
          top: scrollableMessageArea.scrollHeight,
          behavior: 'smooth'
        });
      });
    },
    getMessageIndex(messageElements: Element[], messageId: string): number {
      let i = 0;
      while (i < messageElements.length) {
        for (let j = 0; j < this.conversations.length; j++) {
          const convo = this.conversations[j];
          for (let k = 0; k < convo.messages.length; k++) {
            if (convo.messages[k].id === messageId) {
              return i;
            }
            i += 1;
          }
        }
      }
      return i;
    },
    personalizationSelected(newMessage: string) {
      this.newMessage = newMessage;
    },
    async sendReply() {
      if (this.newMessage.length === 0) {
        return;
      }
      const message = this.message as CommunicationMessage;
      const sendReplyData = {
        message: this.newMessage,
        communicationTypeId: CommunicationType.SMS,
        providerIds: message.providerId ? [message.providerId] : [],
        facilityContactIds: message.facilityContactId ? [message.facilityContactId] : []
      };
      const responseData = await this.httpPost('/communications', 'Failed to send Reply', sendReplyData);
      if (responseData.sentMessageCount === 0) {
        this.$notify({
          group: 'main',
          type: 'error',
          title: 'Error Sending Message',
          text: 'No message was sent. The Contact may have Opted-Out of messages.'
        });
      } else {
        this.newMessage = '';
        const [message] = await (this as any).httpGet(`/communications/conversations/${this.message.id}?lastOccurredOn=${new Date().toISOString()}&pageSize=1`, 'Failed to refresh Conversation.') as ConversationMessage[];
        if (this.conversations.length > 0 && message.threadId === this.conversations[this.conversations.length - 1].threadId) {
          this.conversations[this.conversations.length - 1].messages.push(this.translateMessage(message));
          this.scrollMessageIntoView(message.id);
          return;
        }
        const conversation = { threadId: message.threadId, title: message.threadTitle || 'No Campaign', messages: [] } as UiConversation;
        this.conversations.push(conversation);
        conversation.messages.push(this.translateMessage(message));
        this.scrollMessageIntoView(message.id);
      }
    }
  }
})



// WEBPACK FOOTER //
// ./src/components/communications/cards/MessageConvo.ts